/**
 * Chart Context Registry
 *
 * Central allowlist of valid context groups and chart names.
 * All chart context identifiers MUST be declared here to prevent
 * duplicate or arbitrary strings scattered across components.
 *
 * When adding a new chart to the Advisor integration:
 * 1. Add a new entry to CHART_CONTEXT_GROUPS (if the section is new) — both
 *    `name` and `description` are required; TypeScript will error if either is missing.
 * 2. Add chart name entries to CHART_NAMES
 * 3. Use the constants in the component that creates the chartContextId prop
 *    and in the layout that sets contextGroup on BeaconChartWithLegend
 *
 * @see ARCHITECTURE.md for full design documentation.
 */

// ---------------------------------------------------------------------------
// Context Groups (sections)
// ---------------------------------------------------------------------------

/**
 * Valid context group definitions.
 * Each entry holds a `name` (the string ID used at runtime) and a `description`
 * (sent with the Ask Advisor payload). Both fields are mandatory — TypeScript
 * enforces this at the definition site so a missing description is a compile error.
 *
 * Layouts use `.name` when passing to ChartContextProvider / ChartContextGroupProvider:
 *   <ChartContextProvider group={CHART_CONTEXT_GROUPS.TRAFFIC.name}>
 *
 * Use `getChartContextGroup(id)` to resolve a group object from a runtime string ID.
 */
export const CHART_CONTEXT_GROUPS = {
  /** Traffic section: donut + comparison/current bar charts */
  TRAFFIC: { name: 'traffic', description: 'Donut and bar charts for total traffic' },
  /** Total Traffic section: spline chart showing total traffic over time */
  TOTAL_TRAFFIC: { name: 'total-traffic', description: 'Spline chart for total traffic over time' },
  /** Share of Total Traffic section: spline chart showing market share % */
  SHARE_OF_TRAFFIC: { name: 'share-of-traffic', description: 'Spline chart for share of total traffic over time' },
  /** Traffic by Keyword section: column chart grouped by keyword */
  TRAFFIC_BY_KEYWORD: { name: 'traffic-by-keyword', description: 'Column chart for traffic grouped by keyword' },
  /** Branded/Unbranded Traffic section: spline chart for branded vs unbranded share */
  BRANDED_UNBRANDED_TRAFFIC: {
    name: 'branded-unbranded-traffic',
    description: 'Spline chart for branded vs unbranded traffic share'
  },
  /** Top Entity Traffic section: column chart with double dropdown (entity + metric) */
  TRAFFIC_TOP_ENTITY: {
    name: 'traffic-top-entity',
    description: 'Column chart for top entity traffic with double dropdown'
  },
  /** Brand → Category/Subcategory top entity chart with double dropdown */
  TRAFFIC_TOP_ENTITY_BRAND: {
    name: 'traffic-top-entity-brand',
    description: 'Column chart for brand traffic by category/subcategory'
  },
  /** Filterable Stacked Bar section (keyword): stacked/grouped column chart with brand filtering */
  FILTERABLE_STACKED_BAR: {
    name: 'filterable-stacked-bar',
    description: 'Filterable stacked bar chart for traffic by keyword'
  },
  /** Filterable Stacked Bar section (category/subcategory): stacked/grouped column chart */
  FILTERABLE_STACKED_BAR_CATEGORY: {
    name: 'filterable-stacked-bar-category',
    description: 'Filterable stacked bar chart for traffic by category/subcategory'
  },
  /** Keyword Count with Total Traffic section: spline chart showing keyword count over time */
  KEYWORD_COUNT_TRAFFIC: {
    name: 'keyword-count-traffic',
    description: 'Spline chart for keyword count with total traffic over time'
  },

  // ── Organic Traffic V2 sections ──

  /** Organic Traffic spline chart */
  ORGANIC_V2_TRAFFIC: { name: 'organic-v2-traffic', description: 'Spline chart for organic traffic over time' },
  /** Share of Organic Traffic spline chart */
  ORGANIC_V2_SHARE: { name: 'organic-v2-share', description: 'Spline chart for share of organic traffic over time' },
  /** Keyword single dropdown top entity chart */
  ORGANIC_V2_TOP_ENTITY_KEYWORD: {
    name: 'organic-v2-top-entity-keyword',
    description: 'Column chart for organic traffic by keyword (single dropdown)'
  },
  /** Double dropdown top entity chart (category/brand) */
  ORGANIC_V2_TOP_ENTITY: {
    name: 'organic-v2-top-entity',
    description: 'Column chart for organic traffic top entity with double dropdown'
  },
  /** Filterable stacked bar chart (keyword) */
  ORGANIC_V2_FILTERABLE_STACKED_BAR_KEYWORD: {
    name: 'organic-v2-filterable-stacked-bar-keyword',
    description: 'Filterable stacked bar chart for organic traffic by keyword'
  },
  /** Keyword Count with Organic Traffic spline chart */
  ORGANIC_V2_KEYWORD_COUNT: {
    name: 'organic-v2-keyword-count',
    description: 'Spline chart for keyword count with organic traffic over time'
  },
  /** Double dropdown top entity chart (company-level) */
  ORGANIC_V2_TOP_ENTITY_COMPANY: {
    name: 'organic-v2-top-entity-company',
    description: 'Column chart for organic traffic top entity (company-level)'
  },
  /** Double dropdown top entity chart (brand → category/subcategory) */
  ORGANIC_V2_TOP_ENTITY_BRAND: {
    name: 'organic-v2-top-entity-brand',
    description: 'Column chart for organic traffic by brand category/subcategory'
  },
  /** Branded/Unbranded organic traffic spline chart */
  ORGANIC_V2_BRANDED_UNBRANDED: {
    name: 'organic-v2-branded-unbranded',
    description: 'Spline chart for branded vs unbranded organic traffic share'
  },
  /** Filterable stacked bar chart (category/subcategory) */
  ORGANIC_V2_FILTERABLE_STACKED_BAR_CATEGORY: {
    name: 'organic-v2-filterable-stacked-bar-category',
    description: 'Filterable stacked bar chart for organic traffic by category/subcategory'
  },

  // ── Paid Traffic sections ──

  /** Paid Search Traffic spline chart */
  PAID_TRAFFIC: { name: 'paid-traffic', description: 'Spline chart for paid search traffic over time' },
  /** Paid Search Spend spline chart */
  PAID_TRAFFIC_AD_SPEND: { name: 'paid-traffic-ad-spend', description: 'Spline chart for paid search spend over time' },
  /** Share of Paid Search Traffic spline chart */
  PAID_TRAFFIC_SHARE: {
    name: 'paid-traffic-share',
    description: 'Spline chart for share of paid search traffic over time'
  },
  /** Top Entity Paid Traffic column chart with double dropdown */
  PAID_TRAFFIC_TOP_ENTITY: {
    name: 'paid-traffic-top-entity',
    description: 'Column chart for top entity paid traffic with double dropdown'
  },
  /** Products with Paid Search Traffic spline chart */
  PAID_TRAFFIC_PRODUCTS: {
    name: 'paid-traffic-products',
    description: 'Spline chart for products with paid search traffic over time'
  },
  /** Keyword Count with Paid Search Traffic spline chart */
  PAID_TRAFFIC_KEYWORD_COUNT: {
    name: 'paid-traffic-keyword-count',
    description: 'Spline chart for keyword count with paid search traffic over time'
  },
  /** Branded/Unbranded Paid Traffic spline chart */
  PAID_TRAFFIC_BRANDED_UNBRANDED: {
    name: 'paid-traffic-branded-unbranded',
    description: 'Spline chart for branded vs unbranded paid traffic share'
  },
  /** Filterable Stacked Bar for paid traffic by keyword */
  PAID_TRAFFIC_FILTERABLE_STACKED_BAR: {
    name: 'paid-traffic-filterable-stacked-bar',
    description: 'Filterable stacked bar chart for paid traffic by keyword'
  },
  /** Brand → Category/Subcategory top entity chart for paid traffic */
  PAID_TRAFFIC_TOP_ENTITY_BRAND: {
    name: 'paid-traffic-top-entity-brand',
    description: 'Column chart for brand paid traffic by category/subcategory'
  },
  /** Filterable Stacked Bar for paid traffic by category/subcategory */
  PAID_TRAFFIC_FILTERABLE_STACKED_BAR_CATEGORY: {
    name: 'paid-traffic-filterable-stacked-bar-category',
    description: 'Filterable stacked bar chart for paid traffic by category/subcategory'
  },

  // ── Other Traffic sections ──

  /** Other Traffic spline chart */
  OTHER_TRAFFIC: { name: 'other-traffic', description: 'Spline chart for other traffic over time' },
  /** Products with Other Traffic spline chart */
  OTHER_TRAFFIC_PRODUCTS: {
    name: 'other-traffic-products',
    description: 'Spline chart for products with other traffic over time'
  },

  // ── Sales Summary sections ──

  /** Retail Sales spline chart (1P/3P selector in 1P branch, plain in non-1P branch) */
  RETAIL_SALES: { name: 'retail-sales', description: 'Spline chart for retail sales over time' },
  /** Market Share of Retail Sales spline chart (multiple field options) */
  MARKET_SHARE_RETAIL_SALES: {
    name: 'market-share-retail-sales',
    description: 'Spline chart for market share of retail sales over time'
  },
  /** Product Count spline chart */
  SALES_PRODUCT_COUNT: { name: 'sales-product-count', description: 'Spline chart for product count over time' },
  /** 1P Retail Sales as % of Total spline chart */
  SALES_1P_PERCENT: {
    name: 'sales-1p-percent',
    description: 'Spline chart for 1P retail sales as percentage of total'
  },
  /** Market Share column chart (both 1P and non-1P variants) */
  SALES_MARKET_SHARE_COLUMN: {
    name: 'sales-market-share-column',
    description: 'Column chart for market share of retail sales by entity'
  },
  /** Top entity double dropdown chart (company-level, 1P branch) */
  SALES_TOP_ENTITY: {
    name: 'sales-top-entity',
    description: 'Column chart for top entity retail sales with double dropdown'
  },
  /** Top entity double dropdown chart (category/brand/etc. and non-1P) */
  SALES_TOP_ENTITY_BROAD: {
    name: 'sales-top-entity-broad',
    description: 'Column chart for top entity retail sales by category/brand'
  },
  /** Product market share spline chart (product entity only) */
  SALES_MARKET_SHARE_PRODUCT: {
    name: 'sales-market-share-product',
    description: 'Spline chart for product market share of retail sales'
  },

  // ── Conversion sections ──

  /** Conversion rate spline chart */
  CONVERSION_SPLINE: { name: 'conversion-spline', description: 'Spline chart for conversion rate over time' },
  /** Conversion top entity column chart */
  CONVERSION_TOP_ENTITY: {
    name: 'conversion-top-entity',
    description: 'Column chart for conversion rate by top entity'
  },

  // ── Price Summary sections ──

  /** Retail Price spline chart */
  PRICE_RETAIL_PRICE: { name: 'price-retail-price', description: 'Spline chart for retail price over time' },
  /** Price Index spline chart */
  PRICE_INDEX: { name: 'price-index', description: 'Spline chart for price index over time' },
  /** Price top entity column chart */
  PRICE_TOP_ENTITY: {
    name: 'price-top-entity',
    description: 'Column chart for price by top entity with double dropdown'
  },

  // ── Price Promotion sections ──

  /** Promotions top section: donut chart + spline chart */
  PROMOTION_TOP_SECTION: {
    name: 'promotion-top-section',
    description: 'Donut and spline charts for promotion count'
  },
  /** Promotions top entity column chart (entity dropdown) */
  PROMOTION_TOP_ENTITY: { name: 'promotion-top-entity', description: 'Column chart for promotions by top entity' },
  /** Promotions deal type column chart (deal type dropdown) */
  PROMOTION_DEAL_TYPE: { name: 'promotion-deal-type', description: 'Column chart for promotions by deal type' },

  // ── Units Sold sections ──

  /** Units Sold spline chart (1P/3P or plain) */
  UNITS_SOLD_SPLINE: { name: 'units-sold-spline', description: 'Spline chart for units sold over time' },
  /** Market Share of Units Sold spline chart */
  UNITS_SOLD_MARKET_SHARE: {
    name: 'units-sold-market-share',
    description: 'Spline chart for market share of units sold over time'
  },
  /** Product Count spline chart (units sold context) */
  UNITS_SOLD_PRODUCT_COUNT: {
    name: 'units-sold-product-count',
    description: 'Spline chart for product count (units sold context)'
  },
  /** 1P Total Units Sold spline chart (company-level) */
  UNITS_SOLD_1P_TOTAL: {
    name: 'units-sold-1p-total',
    description: 'Spline chart for 1P total units sold (company-level)'
  },
  /** Market Share column chart for units sold */
  UNITS_SOLD_MARKET_SHARE_COLUMN: {
    name: 'units-sold-market-share-column',
    description: 'Column chart for market share of units sold by entity'
  },
  /** Top entity double dropdown chart for units sold (company) */
  UNITS_SOLD_TOP_ENTITY: {
    name: 'units-sold-top-entity',
    description: 'Column chart for top entity units sold with double dropdown (company)'
  },
  /** Top entity double dropdown chart for units sold (broad) */
  UNITS_SOLD_TOP_ENTITY_BROAD: {
    name: 'units-sold-top-entity-broad',
    description: 'Column chart for top entity units sold with double dropdown (broad)'
  },
  /** 1P Total Units Sold spline chart (broad entities) */
  UNITS_SOLD_1P_TOTAL_BROAD: {
    name: 'units-sold-1p-total-broad',
    description: 'Spline chart for 1P total units sold (broad entities)'
  },
  /** Product market share spline chart for units sold */
  UNITS_SOLD_MARKET_SHARE_PRODUCT: {
    name: 'units-sold-market-share-product',
    description: 'Spline chart for product market share of units sold'
  },
  /** Non-1P market share spline chart for units sold */
  UNITS_SOLD_NON1P_MARKET_SHARE: {
    name: 'units-sold-non1p-market-share',
    description: 'Spline chart for non-1P market share of units sold'
  },
  /** Non-1P market share column chart for units sold */
  UNITS_SOLD_NON1P_MARKET_SHARE_COLUMN: {
    name: 'units-sold-non1p-market-share-column',
    description: 'Column chart for non-1P market share of units sold by entity'
  },
  /** Non-1P top entity double dropdown chart for units sold */
  UNITS_SOLD_NON1P_TOP_ENTITY: {
    name: 'units-sold-non1p-top-entity',
    description: 'Column chart for non-1P top entity units sold with double dropdown'
  },

  // ── Beacon Sales sections ──

  BEACON_RETAIL_SALES: { name: 'beacon-retail-sales', description: 'Spline chart for retail sales over time' },
  BEACON_WHOLESALE_SALES: { name: 'beacon-wholesale-sales', description: 'Spline chart for wholesale sales over time' },
  BEACON_RETAIL_SALES_BY_ENTITY: { name: 'beacon-retail-sales-by-entity', description: 'Column chart for retail sales by entity' },
  BEACON_UNITS_SOLD: { name: 'beacon-units-sold', description: 'Spline chart for units sold over time' },
  BEACON_UNITS_SOLD_RETAIL_PRICE: { name: 'beacon-units-sold-retail-price', description: 'Spline chart for retail price (units sold context)' },
  BEACON_UNITS_SOLD_BY_ENTITY: { name: 'beacon-units-sold-by-entity', description: 'Column chart for units sold by entity' },
  BEACON_PRODUCTS_WITH_UNITS_SOLD: { name: 'beacon-products-with-units-sold', description: 'Spline chart for products with units sold' },
  BEACON_PRODUCTS_WITH_RETAIL_SALES: { name: 'beacon-products-with-retail-sales', description: 'Spline chart for products with retail sales' },
  BEACON_RETURN_RATE: { name: 'beacon-return-rate', description: 'Spline chart for return rate over time' },
  BEACON_UNITS_RETURNED: { name: 'beacon-units-returned', description: 'Spline chart for units returned over time' },
  BEACON_RETURN_RETAIL_VALUE: { name: 'beacon-return-retail-value', description: 'Spline chart for return retail value over time' },
  BEACON_RETURN_RATE_BY_ENTITY: { name: 'beacon-return-rate-by-entity', description: 'Column chart for return rate by entity' },
  BEACON_RETAILER_GROSS_MARGIN: { name: 'beacon-retailer-gross-margin', description: 'Spline chart for retailer gross margin' },
  BEACON_RETAILER_GROSS_MARGIN_PERCENT: { name: 'beacon-retailer-gross-margin-percent', description: 'Spline chart for retailer gross margin %' },
  BEACON_BRAND_COGS: { name: 'beacon-brand-cogs', description: 'Spline chart for brand COGS' },
  BEACON_BRAND_GROSS_MARGIN: { name: 'beacon-brand-gross-margin', description: 'Spline chart for brand gross margin' },
  BEACON_BRAND_GROSS_MARGIN_PERCENT: { name: 'beacon-brand-gross-margin-percent', description: 'Spline chart for brand gross margin %' },

  // ── Beacon Price sections ──

  BEACON_RETAIL_PRICE: { name: 'beacon-retail-price', description: 'Spline chart for retail price over time' },
  BEACON_WHOLESALE_PRICE: { name: 'beacon-wholesale-price', description: 'Spline chart for wholesale price over time' },
  BEACON_BRAND_COGS_PER_UNIT: { name: 'beacon-brand-cogs-per-unit', description: 'Spline chart for brand COGS per unit' },
  BEACON_BRAND_GROSS_MARGIN_PER_UNIT: { name: 'beacon-brand-gross-margin-per-unit', description: 'Spline chart for brand gross margin per unit' },
  BEACON_RETAIL_PRICE_BY_ENTITY: { name: 'beacon-retail-price-by-entity', description: 'Column chart for retail price by entity' },
  BEACON_PROMOTION_COUNT: { name: 'beacon-promotion-count', description: 'Donut and spline chart for promotion count' },
  BEACON_PROMOTION_TOP_ENTITY: { name: 'beacon-promotion-top-entity', description: 'Column chart for promotions by entity' },
  BEACON_PROMOTION_DEAL_TYPE: { name: 'beacon-promotion-deal-type', description: 'Column chart for promotions by deal type' },

  // ── Beacon Review sections ──

  BEACON_REVIEW_SUMMARY: { name: 'beacon-review-summary', description: 'Key metrics cards for review summary' },
  BEACON_REVIEW_QUALITY_SCORE: { name: 'beacon-review-quality-score', description: 'Donut and bar chart for review quality score' },
  BEACON_WEEKLY_REVIEW_VOLUME: { name: 'beacon-weekly-review-volume', description: 'Spline chart for weekly review volume' },
  BEACON_WEEKLY_REVIEW_BY_STAR: { name: 'beacon-weekly-review-by-star', description: 'Stacked chart for weekly review volume by star rating' },
  BEACON_LIFETIME_REVIEW_BY_STAR: { name: 'beacon-lifetime-review-by-star', description: 'Bar chart for lifetime review volume by star rating' },
  BEACON_REVIEW_CATALOG_COUNT_VOLUME: { name: 'beacon-review-catalog-count-volume', description: 'Bar chart for product catalog count by review volume' },
  BEACON_REVIEW_CATALOG_COUNT_RATING: { name: 'beacon-review-catalog-count-rating', description: 'Bar chart for product catalog count by review rating' },
  BEACON_REVIEW_BY_CATEGORY: { name: 'beacon-review-by-category', description: 'Column chart for reviews by category' },
  BEACON_REVIEW_BY_SUBCATEGORY: { name: 'beacon-review-by-subcategory', description: 'Column chart for reviews by subcategory' },
  BEACON_LIFETIME_RATING_BY_STAR: { name: 'beacon-lifetime-rating-by-star', description: 'Bar chart for lifetime rating volume by star rating' },
  BEACON_RATING_CATALOG_COUNT_VOLUME: { name: 'beacon-rating-catalog-count-volume', description: 'Bar chart for product catalog count by rating volume' },
  BEACON_RATING_CATALOG_COUNT_RATING: { name: 'beacon-rating-catalog-count-rating', description: 'Bar chart for product catalog count by rating' },
  BEACON_RATING_BY_CATEGORY: { name: 'beacon-rating-by-category', description: 'Column chart for ratings by category' },
  BEACON_RATING_BY_SUBCATEGORY: { name: 'beacon-rating-by-subcategory', description: 'Column chart for ratings by subcategory' },
  BEACON_SENTIMENT_OVERVIEW: { name: 'beacon-sentiment-overview', description: 'Donut and column charts for review sentiment' },
  BEACON_SENTIMENT_WEEKLY_VOLUME: { name: 'beacon-sentiment-weekly-volume', description: 'Stacked chart for weekly review volume by sentiment' },
  BEACON_SENTIMENT_BY_TOPIC: { name: 'beacon-sentiment-by-topic', description: 'Column chart for sentiment by topic' },
  BEACON_SENTIMENT_POSITIVE_KEYWORDS: { name: 'beacon-sentiment-positive-keywords', description: 'Column chart for keywords in positive reviews' },
  BEACON_SENTIMENT_NEGATIVE_KEYWORDS: { name: 'beacon-sentiment-negative-keywords', description: 'Column chart for keywords in negative reviews' },
  BEACON_SENTIMENT_BY_CATEGORY: { name: 'beacon-sentiment-by-category', description: 'Stacked chart for sentiment by category' },
  BEACON_SENTIMENT_BY_SUBCATEGORY: { name: 'beacon-sentiment-by-subcategory', description: 'Stacked chart for sentiment by subcategory' },

  // ── Beacon Traffic sections ──

  BEACON_TOTAL_TRAFFIC_TOP: { name: 'beacon-total-traffic-top', description: 'Donut and bar charts for total traffic breakdown' },
  BEACON_TOTAL_TRAFFIC_SPLINE: { name: 'beacon-total-traffic-spline', description: 'Spline chart for total traffic over time' },
  BEACON_TOTAL_TRAFFIC_BY_TOPIC: { name: 'beacon-total-traffic-by-topic', description: 'Bar chart for total traffic by topic' },
  BEACON_TOTAL_TRAFFIC_BY_KEYWORD: { name: 'beacon-total-traffic-by-keyword', description: 'Bar chart for total traffic by keyword' },
  BEACON_KEYWORDS_WITH_TRAFFIC: { name: 'beacon-keywords-with-traffic', description: 'Spline chart for keywords with traffic' },
  BEACON_TOTAL_TRAFFIC_BY_CATEGORY: { name: 'beacon-total-traffic-by-category', description: 'Bar chart for total traffic by category' },
  BEACON_TOTAL_TRAFFIC_BY_SUBCATEGORY: { name: 'beacon-total-traffic-by-subcategory', description: 'Bar chart for total traffic by subcategory' },
  BEACON_TOTAL_TRAFFIC_COLUMN: { name: 'beacon-total-traffic-column', description: 'Column chart for total traffic by entity' },
  BEACON_ORGANIC_TRAFFIC_SPLINE: { name: 'beacon-organic-traffic-spline', description: 'Spline chart for organic traffic over time' },
  BEACON_ORGANIC_TRAFFIC_BAR: { name: 'beacon-organic-traffic-bar', description: 'Bar chart for organic traffic by keyword' },
  BEACON_PRODUCTS_WITH_ORGANIC: { name: 'beacon-products-with-organic', description: 'Spline chart for products with organic traffic' },
  BEACON_KEYWORDS_WITH_ORGANIC: { name: 'beacon-keywords-with-organic', description: 'Spline chart for keywords with organic traffic' },
  BEACON_PAID_SEARCH_METRICS: { name: 'beacon-paid-search-metrics', description: 'Key metric cards for paid search' },
  BEACON_PAID_SEARCH_BAR: { name: 'beacon-paid-search-bar', description: 'Bar chart for paid search by entity' },
  BEACON_PAID_SEARCH_TREND: { name: 'beacon-paid-search-trend', description: 'Multi-metric trend chart for paid search' },
  BEACON_OTHER_TRAFFIC_SPLINE: { name: 'beacon-other-traffic-spline', description: 'Spline chart for other traffic over time' },
  BEACON_OTHER_TRAFFIC_PRODUCTS: { name: 'beacon-other-traffic-products', description: 'Spline chart for products with other traffic' },
  BEACON_CONVERSION_RATE: { name: 'beacon-conversion-rate', description: 'Spline chart for conversion rate over time' },
  BEACON_DISPLAY_TRAFFIC_METRICS: { name: 'beacon-display-traffic-metrics', description: 'Key metric cards for display traffic' },
  BEACON_DISPLAY_TRAFFIC_TREND: { name: 'beacon-display-traffic-trend', description: 'Trend chart for display traffic' },

  // ── Product Growth section ──

  BEACON_PRODUCT_GROWTH_MATRIX: { name: 'beacon-product-growth-matrix', description: 'Scatter plot matrix for product growth (traffic index vs conversion index)' },

  // ── Paid Search (non-topic, original page) sections ──

  BEACON_PAID_SEARCH_BAR_CHART: { name: 'beacon-paid-search-bar-chart', description: 'Bar chart for paid search by entity' },
  BEACON_PAID_SEARCH_TREND_CHART: { name: 'beacon-paid-search-trend-chart', description: 'Multi-metric trend chart for paid search' },
  BEACON_PAID_SEARCH_BY_TOPIC: { name: 'beacon-paid-search-by-topic', description: 'Bar chart for paid search traffic by topic' },
  BEACON_PAID_SEARCH_BY_KEYWORD: { name: 'beacon-paid-search-by-keyword', description: 'Bar chart for paid search traffic by keyword' },
  BEACON_PAID_SEARCH_BY_CATEGORY: { name: 'beacon-paid-search-by-category', description: 'Bar chart for paid search traffic by category' },
  BEACON_PAID_SEARCH_BY_SUBCATEGORY: { name: 'beacon-paid-search-by-subcategory', description: 'Bar chart for paid search traffic by subcategory' },
  BEACON_PAID_SEARCH_PERCENT_TRAFFIC: { name: 'beacon-paid-search-percent-traffic', description: 'Spline chart for paid search as percent of total traffic' },
  BEACON_PAID_SEARCH_TOPICS_BAR: { name: 'beacon-paid-search-topics-bar', description: 'Bar chart for paid search by entity (topics view)' },
  BEACON_PAID_SEARCH_TOPICS_TREND: { name: 'beacon-paid-search-topics-trend', description: 'Multi-metric trend chart for paid search (topics view)' },
  BEACON_PAID_SEARCH_TOPICS_PERCENT: { name: 'beacon-paid-search-topics-percent', description: 'Spline chart for paid search as percent of total traffic (topics view)' },

  // ── Content V3 sections ──

  BEACON_CONTENT_SPLINE: { name: 'beacon-content-spline', description: 'Spline chart for content score over time' },
  BEACON_CONTENT_BAR: { name: 'beacon-content-bar', description: 'Bar chart for content score by entity' },
  BEACON_CONTENT_KEYWORDS_SPLINE: { name: 'beacon-content-keywords-spline', description: 'Spline chart for keyword content score over time' },
  BEACON_CONTENT_KEYWORDS_BAR: { name: 'beacon-content-keywords-bar', description: 'Bar chart for keyword content score by entity' },
  BEACON_CONTENT_ACCURACY_SPLINE: { name: 'beacon-content-accuracy-spline', description: 'Spline chart for content accuracy over time' },
  BEACON_CONTENT_ACCURACY_BAR: { name: 'beacon-content-accuracy-bar', description: 'Bar chart for content accuracy by entity' },
  BEACON_CONTENT_VARIATIONS_SPLINE: { name: 'beacon-content-variations-spline', description: 'Spline chart for content variation metrics over time' },
  BEACON_CONTENT_VARIATIONS_BAR: { name: 'beacon-content-variations-bar', description: 'Bar chart for content variation metrics by entity' },
  BEACON_CONTENT_VARIATIONS_PARENT_PAGE_SPLINE: { name: 'beacon-content-variations-parent-page-spline', description: 'Spline chart for parent page count over time' },
  BEACON_CONTENT_VARIATIONS_PARENT_PAGE_BAR: { name: 'beacon-content-variations-parent-page-bar', description: 'Bar chart for parent page count by entity' },
  BEACON_CONTENT_VARIATIONS_PAGE_SIZE: { name: 'beacon-content-variations-page-size', description: 'Bar chart for variation page size by product' },

  // ── Beacon Forecast sections ──

  BEACON_FORECAST_LINE_CHART: { name: 'beacon-forecast-line-chart', description: 'Spline chart for forecast key metric projections' },
  BEACON_FORECAST_WEEKS_ON_HAND: { name: 'beacon-forecast-weeks-on-hand', description: 'Spline chart for projected weeks on hand' },
  BEACON_FORECAST_TOP_ENTITY: { name: 'beacon-forecast-top-entity', description: 'Column chart for forecast top entity by metric' },

  // ── Beacon Inventory sections ──

  BEACON_INSTOCK_RATE: { name: 'beacon-instock-rate', description: 'Spline chart for in-stock rate over time' },
  BEACON_UNITS_ON_HAND: { name: 'beacon-units-on-hand', description: 'Spline chart for units on hand over time' },
  BEACON_WEEKS_ON_HAND: { name: 'beacon-weeks-on-hand', description: 'Spline chart for weeks on hand over time' },
  BEACON_INVENTORY_RETAIL_VALUE: { name: 'beacon-inventory-retail-value', description: 'Spline chart for inventory retail value over time' },

  // ── Beacon Buy Box sections ──

  BEACON_BUYBOX_WIN_RATE: { name: 'beacon-buybox-win-rate', description: 'Spline chart for buy box win rate / loss rate over time' },
  BEACON_BUYBOX_SELLERS: { name: 'beacon-buybox-sellers', description: 'Column chart for buy box loss rate by seller' }
} as const;

/** Full group object type — a union of all `{ name, description }` entries. */
export type ChartContextGroup = (typeof CHART_CONTEXT_GROUPS)[keyof typeof CHART_CONTEXT_GROUPS];

/** String union of all valid group IDs (the `.name` field of each group). */
export type ChartContextGroupId = ChartContextGroup['name'];

/**
 * Look up a context group object by its runtime string ID.
 * Returns `undefined` when the ID is not registered.
 */
export function getChartContextGroup(id: string): ChartContextGroup | undefined {
  return (Object.values(CHART_CONTEXT_GROUPS) as ChartContextGroup[]).find((g) => g.name === id);
}

// ---------------------------------------------------------------------------
// Chart Names
// ---------------------------------------------------------------------------

/**
 * Valid chart name identifiers.
 * Each name uniquely identifies a chart within the app.
 * These are combined with the app prefix, context group, and URL suffix
 * to form the full chart ID stored in the Zustand store / DynamoDB.
 *
 * Full ID format: {app}#{contextGroup}#{chartName}#{urlSuffix}
 * Example: atlas#traffic#traffic-donut#category/1280?rid=1&tab=trafficV2&subtab=totalClicks
 */
export const CHART_NAMES = {
  /** Donut chart showing total traffic breakdown (organic, paid, other) */
  TRAFFIC_DONUT: 'traffic-donut',
  /** Bar chart for the comparison time period */
  TRAFFIC_BAR_COMPARISON: 'traffic-bar-comparison',
  /** Bar chart for the current time period */
  TRAFFIC_BAR_CURRENT: 'traffic-bar-current',
  /** Spline chart showing total traffic over time */
  TOTAL_TRAFFIC_SPLINE: 'total-traffic-spline',
  /** Spline chart showing share of total traffic over time */
  SHARE_OF_TRAFFIC_SPLINE: 'share-of-traffic-spline',
  /** Column chart showing traffic grouped by keyword */
  TRAFFIC_BY_KEYWORD_COLUMN: 'traffic-by-keyword-column',
  /** Spline chart showing branded vs unbranded traffic share */
  BRANDED_UNBRANDED_SPLINE: 'branded-unbranded-spline',
  /** Column chart showing top entity traffic with double dropdown */
  TRAFFIC_TOP_ENTITY_COLUMN: 'traffic-top-entity-column',
  /** Stacked/grouped column chart with brand filtering */
  FILTERABLE_STACKED_BAR_CHART: 'filterable-stacked-bar-chart',
  /** Spline chart showing keyword count with total traffic over time */
  KEYWORD_COUNT_TRAFFIC_SPLINE: 'keyword-count-traffic-spline',

  // ── Organic Traffic V2 charts ──

  /** Spline chart for organic traffic over time */
  ORGANIC_V2_TRAFFIC_SPLINE: 'organic-v2-traffic-spline',
  /** Spline chart for share of organic traffic over time */
  ORGANIC_V2_SHARE_SPLINE: 'organic-v2-share-spline',
  /** Column chart for organic traffic top entity */
  ORGANIC_V2_TOP_ENTITY_COLUMN: 'organic-v2-top-entity-column',
  /** Spline chart for keyword count with organic traffic over time */
  ORGANIC_V2_KEYWORD_COUNT_SPLINE: 'organic-v2-keyword-count-spline',

  // ── Paid Traffic charts ──

  /** Spline chart for paid search traffic over time */
  PAID_TRAFFIC_SPLINE: 'paid-traffic-spline',
  /** Spline chart for paid search spend over time */
  PAID_TRAFFIC_AD_SPEND_SPLINE: 'paid-traffic-ad-spend-spline',
  /** Spline chart for share of paid search traffic over time */
  PAID_TRAFFIC_SHARE_SPLINE: 'paid-traffic-share-spline',
  /** Column chart for top entity paid traffic with double dropdown */
  PAID_TRAFFIC_TOP_ENTITY_COLUMN: 'paid-traffic-top-entity-column',
  /** Spline chart for products with paid search traffic over time */
  PAID_TRAFFIC_PRODUCTS_SPLINE: 'paid-traffic-products-spline',
  /** Spline chart for keyword count with paid search traffic over time */
  PAID_TRAFFIC_KEYWORD_COUNT_SPLINE: 'paid-traffic-keyword-count-spline',

  // ── Other Traffic charts ──

  /** Spline chart for other traffic over time */
  OTHER_TRAFFIC_SPLINE: 'other-traffic-spline',
  /** Spline chart for products with other traffic over time */
  OTHER_TRAFFIC_PRODUCTS_SPLINE: 'other-traffic-products-spline',

  // ── Sales Summary charts ──

  /** Spline chart for retail sales (1P/3P selector or plain) */
  RETAIL_SALES_SPLINE: 'retail-sales-spline',
  /** Spline chart for market share of retail sales */
  MARKET_SHARE_RETAIL_SALES_SPLINE: 'market-share-retail-sales-spline',
  /** Spline chart for product count */
  SALES_PRODUCT_COUNT_SPLINE: 'sales-product-count-spline',
  /** Spline chart for 1P retail sales as % of total */
  SALES_1P_PERCENT_SPLINE: 'sales-1p-percent-spline',
  /** Column chart for market share of retail sales by entity */
  SALES_MARKET_SHARE_COLUMN_CHART: 'sales-market-share-column-chart',
  /** Column chart for top entity retail sales with double dropdown */
  SALES_TOP_ENTITY_COLUMN: 'sales-top-entity-column',
  /** Spline chart for product market share of retail sales */
  SALES_MARKET_SHARE_PRODUCT_SPLINE: 'sales-market-share-product-spline',

  // ── Conversion charts ──

  /** Spline chart for conversion rate over time */
  CONVERSION_SPLINE_CHART: 'conversion-spline-chart',
  /** Column chart for conversion rate by top entity */
  CONVERSION_TOP_ENTITY_COLUMN: 'conversion-top-entity-column',

  // ── Price Summary charts ──

  /** Spline chart for retail price over time */
  PRICE_RETAIL_PRICE_SPLINE: 'price-retail-price-spline',
  /** Spline chart for price index over time */
  PRICE_INDEX_SPLINE: 'price-index-spline',
  /** Column chart for price by top entity */
  PRICE_TOP_ENTITY_COLUMN: 'price-top-entity-column',

  // ── Price Promotion charts ──

  /** Spline chart for promotions overview */
  PROMOTION_TOP_SECTION_SPLINE: 'promotion-top-section-spline',
  /** Column chart for promotions by top entity */
  PROMOTION_TOP_ENTITY_COLUMN: 'promotion-top-entity-column',
  /** Column chart for promotions by deal type */
  PROMOTION_DEAL_TYPE_COLUMN: 'promotion-deal-type-column',

  // ── Units Sold charts ──

  /** Spline chart for units sold over time */
  UNITS_SOLD_SPLINE_CHART: 'units-sold-spline-chart',
  /** Spline chart for market share of units sold */
  UNITS_SOLD_MARKET_SHARE_SPLINE: 'units-sold-market-share-spline',
  /** Spline chart for 1P total units sold */
  UNITS_SOLD_1P_TOTAL_SPLINE: 'units-sold-1p-total-spline',
  /** Column chart for market share of units sold by entity */
  UNITS_SOLD_MARKET_SHARE_COLUMN_CHART: 'units-sold-market-share-column-chart',
  /** Column chart for top entity units sold */
  UNITS_SOLD_TOP_ENTITY_COLUMN: 'units-sold-top-entity-column',
  /** Spline chart for product market share of units sold */
  UNITS_SOLD_MARKET_SHARE_PRODUCT_SPLINE: 'units-sold-market-share-product-spline',
  /** Spline chart for non-1P market share of units sold */
  UNITS_SOLD_NON1P_MARKET_SHARE_SPLINE: 'units-sold-non1p-market-share-spline',
  /** Column chart for non-1P market share of units sold by entity */
  UNITS_SOLD_NON1P_MARKET_SHARE_COLUMN_CHART: 'units-sold-non1p-market-share-column-chart',
  /** Column chart for non-1P top entity units sold */
  UNITS_SOLD_NON1P_TOP_ENTITY_COLUMN: 'units-sold-non1p-top-entity-column',

  // ── Beacon chart names ──

  BEACON_RETAIL_SALES_SPLINE: 'beacon-retail-sales-spline',
  BEACON_WHOLESALE_SALES_SPLINE: 'beacon-wholesale-sales-spline',
  BEACON_RETAIL_SALES_BY_ENTITY_COLUMN: 'beacon-retail-sales-by-entity-column',
  BEACON_UNITS_SOLD_SPLINE: 'beacon-units-sold-spline',
  BEACON_UNITS_SOLD_RETAIL_PRICE_SPLINE: 'beacon-units-sold-retail-price-spline',
  BEACON_UNITS_SOLD_BY_ENTITY_COLUMN: 'beacon-units-sold-by-entity-column',
  BEACON_PRODUCTS_WITH_UNITS_SOLD_SPLINE: 'beacon-products-with-units-sold-spline',
  BEACON_PRODUCTS_WITH_RETAIL_SALES_SPLINE: 'beacon-products-with-retail-sales-spline',
  BEACON_RETURN_RATE_SPLINE: 'beacon-return-rate-spline',
  BEACON_UNITS_RETURNED_SPLINE: 'beacon-units-returned-spline',
  BEACON_RETURN_RETAIL_VALUE_SPLINE: 'beacon-return-retail-value-spline',
  BEACON_RETURN_RATE_BY_ENTITY_COLUMN: 'beacon-return-rate-by-entity-column',
  BEACON_RETAILER_GROSS_MARGIN_SPLINE: 'beacon-retailer-gross-margin-spline',
  BEACON_RETAILER_GROSS_MARGIN_PERCENT_SPLINE: 'beacon-retailer-gross-margin-percent-spline',
  BEACON_BRAND_COGS_SPLINE: 'beacon-brand-cogs-spline',
  BEACON_BRAND_GROSS_MARGIN_SPLINE: 'beacon-brand-gross-margin-spline',
  BEACON_BRAND_GROSS_MARGIN_PERCENT_SPLINE: 'beacon-brand-gross-margin-percent-spline',
  BEACON_RETAIL_PRICE_SPLINE: 'beacon-retail-price-spline',
  BEACON_WHOLESALE_PRICE_SPLINE: 'beacon-wholesale-price-spline',
  BEACON_BRAND_COGS_PER_UNIT_SPLINE: 'beacon-brand-cogs-per-unit-spline',
  BEACON_BRAND_GROSS_MARGIN_PER_UNIT_SPLINE: 'beacon-brand-gross-margin-per-unit-spline',
  BEACON_RETAIL_PRICE_BY_ENTITY_COLUMN: 'beacon-retail-price-by-entity-column',
  BEACON_TOTAL_TRAFFIC_SPLINE_CHART: 'beacon-total-traffic-spline-chart',
  BEACON_ORGANIC_TRAFFIC_SPLINE_CHART: 'beacon-organic-traffic-spline-chart',
  BEACON_ORGANIC_TRAFFIC_BAR_CHART: 'beacon-organic-traffic-bar-chart',
  BEACON_PRODUCTS_WITH_ORGANIC_SPLINE: 'beacon-products-with-organic-spline',
  BEACON_KEYWORDS_WITH_ORGANIC_SPLINE: 'beacon-keywords-with-organic-spline',
  BEACON_OTHER_TRAFFIC_SPLINE_CHART: 'beacon-other-traffic-spline-chart',
  BEACON_OTHER_TRAFFIC_PRODUCTS_SPLINE: 'beacon-other-traffic-products-spline',
  BEACON_CONVERSION_RATE_SPLINE: 'beacon-conversion-rate-spline',
  BEACON_INSTOCK_RATE_SPLINE: 'beacon-instock-rate-spline',
  BEACON_UNITS_ON_HAND_SPLINE: 'beacon-units-on-hand-spline',
  BEACON_WEEKS_ON_HAND_SPLINE: 'beacon-weeks-on-hand-spline',
  BEACON_INVENTORY_RETAIL_VALUE_SPLINE: 'beacon-inventory-retail-value-spline',

  // ── Beacon Forecast charts ──

  BEACON_FORECAST_LINE_CHART_SPLINE: 'beacon-forecast-line-chart-spline',
  BEACON_FORECAST_WEEKS_ON_HAND_SPLINE: 'beacon-forecast-weeks-on-hand-spline',
  BEACON_FORECAST_TOP_ENTITY_COLUMN: 'beacon-forecast-top-entity-column',

  // ── Beacon Promotion charts ──

  BEACON_PROMOTION_COUNT_DONUT: 'beacon-promotion-count-donut',
  BEACON_PROMOTION_COUNT_SPLINE: 'beacon-promotion-count-spline',
  BEACON_PROMOTION_TOP_ENTITY_COLUMN: 'beacon-promotion-top-entity-column',
  BEACON_PROMOTION_DEAL_TYPE_COLUMN: 'beacon-promotion-deal-type-column',

  // ── Beacon Total Traffic (multi-chart) charts ──

  BEACON_TOTAL_TRAFFIC_DONUT: 'beacon-total-traffic-donut',
  BEACON_TOTAL_TRAFFIC_COLUMN_CHART: 'beacon-total-traffic-column-chart',
  BEACON_KEYWORDS_WITH_TRAFFIC_SPLINE: 'beacon-keywords-with-traffic-spline',
  BEACON_TOTAL_TRAFFIC_BY_TOPIC_BAR: 'beacon-total-traffic-by-topic-bar',
  BEACON_TOTAL_TRAFFIC_BY_KEYWORD_BAR: 'beacon-total-traffic-by-keyword-bar',
  BEACON_TOTAL_TRAFFIC_BY_CATEGORY_BAR: 'beacon-total-traffic-by-category-bar',
  BEACON_TOTAL_TRAFFIC_BY_SUBCATEGORY_BAR: 'beacon-total-traffic-by-subcategory-bar',

  // ── Beacon Review charts ──

  BEACON_REVIEW_QUALITY_SCORE_COLUMN: 'beacon-review-quality-score-column',
  BEACON_WEEKLY_REVIEW_VOLUME_SPLINE: 'beacon-weekly-review-volume-spline',
  BEACON_WEEKLY_REVIEW_BY_STAR_COLUMN: 'beacon-weekly-review-by-star-column',
  BEACON_LIFETIME_REVIEW_BY_STAR_COLUMN: 'beacon-lifetime-review-by-star-column',
  BEACON_REVIEW_CATALOG_COUNT_VOLUME_COLUMN: 'beacon-review-catalog-count-volume-column',
  BEACON_REVIEW_CATALOG_COUNT_RATING_COLUMN: 'beacon-review-catalog-count-rating-column',
  BEACON_REVIEW_BY_CATEGORY_COLUMN: 'beacon-review-by-category-column',
  BEACON_REVIEW_BY_SUBCATEGORY_COLUMN: 'beacon-review-by-subcategory-column',
  BEACON_LIFETIME_RATING_BY_STAR_COLUMN: 'beacon-lifetime-rating-by-star-column',
  BEACON_RATING_CATALOG_COUNT_VOLUME_COLUMN: 'beacon-rating-catalog-count-volume-column',
  BEACON_RATING_CATALOG_COUNT_RATING_COLUMN: 'beacon-rating-catalog-count-rating-column',
  BEACON_RATING_BY_CATEGORY_COLUMN: 'beacon-rating-by-category-column',
  BEACON_RATING_BY_SUBCATEGORY_COLUMN: 'beacon-rating-by-subcategory-column',
  BEACON_SENTIMENT_OVERVIEW_COLUMN: 'beacon-sentiment-overview-column',
  BEACON_SENTIMENT_WEEKLY_VOLUME_COLUMN: 'beacon-sentiment-weekly-volume-column',
  BEACON_SENTIMENT_BY_TOPIC_COLUMN: 'beacon-sentiment-by-topic-column',
  BEACON_SENTIMENT_POSITIVE_KEYWORDS_COLUMN: 'beacon-sentiment-positive-keywords-column',
  BEACON_SENTIMENT_NEGATIVE_KEYWORDS_COLUMN: 'beacon-sentiment-negative-keywords-column',
  BEACON_SENTIMENT_BY_CATEGORY_COLUMN: 'beacon-sentiment-by-category-column',
  BEACON_SENTIMENT_BY_SUBCATEGORY_COLUMN: 'beacon-sentiment-by-subcategory-column',

  // ── Product Growth charts ──

  BEACON_PRODUCT_GROWTH_MATRIX_SCATTER: 'beacon-product-growth-matrix-scatter',

  // ── Paid Search charts ──

  BEACON_PAID_SEARCH_BAR_CHART_COLUMN: 'beacon-paid-search-bar-chart-column',
  BEACON_PAID_SEARCH_TREND_CHART_SPLINE: 'beacon-paid-search-trend-chart-spline',
  BEACON_PAID_SEARCH_BY_TOPIC_BAR: 'beacon-paid-search-by-topic-bar',
  BEACON_PAID_SEARCH_BY_KEYWORD_BAR: 'beacon-paid-search-by-keyword-bar',
  BEACON_PAID_SEARCH_BY_CATEGORY_BAR: 'beacon-paid-search-by-category-bar',
  BEACON_PAID_SEARCH_BY_SUBCATEGORY_BAR: 'beacon-paid-search-by-subcategory-bar',
  BEACON_PAID_SEARCH_PERCENT_TRAFFIC_SPLINE: 'beacon-paid-search-percent-traffic-spline',
  BEACON_PAID_SEARCH_TOPICS_BAR_COLUMN: 'beacon-paid-search-topics-bar-column',
  BEACON_PAID_SEARCH_TOPICS_TREND_SPLINE: 'beacon-paid-search-topics-trend-spline',
  BEACON_PAID_SEARCH_TOPICS_PERCENT_SPLINE: 'beacon-paid-search-topics-percent-spline',

  // ── Content V3 charts ──

  BEACON_CONTENT_SPLINE_CHART: 'beacon-content-spline-chart',
  BEACON_CONTENT_BAR_CHART: 'beacon-content-bar-chart',
  BEACON_CONTENT_KEYWORDS_SPLINE_CHART: 'beacon-content-keywords-spline-chart',
  BEACON_CONTENT_KEYWORDS_BAR_CHART: 'beacon-content-keywords-bar-chart',
  BEACON_CONTENT_ACCURACY_SPLINE_CHART: 'beacon-content-accuracy-spline-chart',
  BEACON_CONTENT_ACCURACY_BAR_CHART: 'beacon-content-accuracy-bar-chart',
  BEACON_CONTENT_VARIATIONS_SPLINE_CHART: 'beacon-content-variations-spline-chart',
  BEACON_CONTENT_VARIATIONS_BAR_CHART: 'beacon-content-variations-bar-chart',
  BEACON_CONTENT_VARIATIONS_PARENT_PAGE_SPLINE_CHART: 'beacon-content-variations-parent-page-spline-chart',
  BEACON_CONTENT_VARIATIONS_PARENT_PAGE_BAR_CHART: 'beacon-content-variations-parent-page-bar-chart',
  BEACON_CONTENT_VARIATIONS_PAGE_SIZE_CHART: 'beacon-content-variations-page-size-chart',

  // ── Buy Box charts ──

  BEACON_BUYBOX_WIN_RATE_SPLINE: 'beacon-buybox-win-rate-spline',
  BEACON_BUYBOX_SELLERS_COLUMN: 'beacon-buybox-sellers-column'
} as const;

export type ChartName = (typeof CHART_NAMES)[keyof typeof CHART_NAMES];
